import React, { useMemo, useEffect, useState } from "react";
import { CheckCircle, ArrowClockwise, PencilSimple } from "@phosphor-icons/react";

/**
 * Iter117 — Wizard per Prezzo Flessibile lato marketplace.
 * Iter209h — Supporto selezione multipla per domanda (`allow_multiple_answers`).
 *
 * Per una domanda multi-select, `pricingAnswers[i]` ha forma:
 *   {question_id, question_label, answer_ids: [id, ...],
 *    answer_labels: [label, ...], selected_answers: [{id, label, price_modifier}],
 *    is_multi_answer: true}
 * Per una domanda single-select resta l'entry classica con `answer_id` + `answer_label`.
 *
 * Navigazione: la domanda successiva è quella collegata alla PRIMA risposta
 * selezionata (posizione più bassa in `q.answers[]`).
 */

const sid = (v) => (v === null || v === undefined ? "" : String(v));

// Ritorna l'id "primario" per la navigazione ai figli:
//  - se single-select: answer_id
//  - se multi-select: l'id con posizione più bassa in question.answers[]
function primaryAnswerId(answered, question) {
  if (!answered) return "";
  if (answered.answer_id) return sid(answered.answer_id);
  const ids = Array.isArray(answered.answer_ids) ? answered.answer_ids.map(sid) : [];
  if (ids.length === 0) return "";
  const answers = question?.answers || [];
  for (const a of answers) {
    if (ids.includes(sid(a.id))) return sid(a.id);
  }
  return ids[0];
}

// Calcola il delta EUR di UNA singola domanda a partire dall'entry answered
// e dal `running` corrente. Applica la regola: prima € (somma), poi %
// (somma) sul subtotale.
function computeQuestionDelta(answered, question, running) {
  if (!answered) return 0;
  const answers = question?.answers || [];
  // Multi
  if (Array.isArray(answered.answer_ids) && answered.answer_ids.length > 0) {
    const selectedSet = new Set(answered.answer_ids.map(sid));
    const selectedDefs = answers.filter((a) => selectedSet.has(sid(a.id)));
    let eurSum = 0;
    let pctSum = 0;
    for (const a of selectedDefs) {
      const mod = a.price_modifier || {};
      const type = (mod.type || "").toLowerCase();
      const value = Number(mod.value) || 0;
      if (type === "eur") eurSum += value;
      else if (type === "pct") pctSum += value;
    }
    const pctClamped = Math.max(-80, Math.min(200, pctSum));
    const subAfterEur = Math.round((running + eurSum) * 100) / 100;
    const pctDelta = Math.round(subAfterEur * (pctClamped / 100) * 100) / 100;
    return Math.round((eurSum + pctDelta) * 100) / 100;
  }
  // Single
  const ansDef = answers.find((a) => sid(a.id) === sid(answered.answer_id));
  if (!ansDef) return 0;
  const mod = ansDef.price_modifier || {};
  const type = (mod.type || "").toLowerCase();
  const value = Number(mod.value) || 0;
  if (type === "pct") {
    const capped = Math.max(-80, Math.min(200, value));
    return Math.round(running * (capped / 100) * 100) / 100;
  }
  if (type === "eur") {
    return Math.round(value * 100) / 100;
  }
  return 0;
}


/**
 * Iter209ab — Calcola la lista dei supplementi accumulati durante il flusso
 * di personalizzazione, uno per ciascuna domanda che ha prodotto un delta
 * non nullo.
 *
 * Usata dall'"Anteprima Costi" per mostrare ogni supplemento come riga
 * separata (label = etichetta risposta scelta, amount = delta EUR) e per
 * aggiornare il totale.
 *
 * Sorgente unica di verità: usa esattamente lo stesso `computeQuestionDelta`
 * del wizard, quindi i valori sono consistenti tra preview inline e modale.
 *
 * @param {object} service  service con `has_flexible_price` + `pricing_logic`.
 * @param {Array}  answers  `pricingAnswers` come stato del wizard.
 * @returns {Array<{question_id, label, amount}>} solo entries con `amount != 0`.
 */
export function computeFlexibleSupplements(service, answers) {
  if (!service?.has_flexible_price) return [];
  const logic = Array.isArray(service?.pricing_logic) ? service.pricing_logic : [];
  if (logic.length === 0 || !Array.isArray(answers) || answers.length === 0) return [];
  const byId = new Map(logic.filter((q) => q?.id).map((q) => [sid(q.id), q]));
  const supplements = [];
  let running = Number(service.base_price) || 0;
  for (const a of answers) {
    if (!a || !a.question_id) continue;
    const q = byId.get(sid(a.question_id));
    if (!q) continue;
    const d = computeQuestionDelta(a, q, running);
    running = Math.round((running + d) * 100) / 100;
    if (!d) continue;
    // Etichetta preferita: label della risposta (multi = join). Fallback al testo della domanda.
    let label = "";
    if (Array.isArray(a.answer_labels) && a.answer_labels.length > 0) {
      label = a.answer_labels.filter(Boolean).join(", ");
    } else if (a.answer_label) {
      label = a.answer_label;
    }
    if (!label) label = a.question_label || q.label || "Supplemento";
    supplements.push({
      question_id: sid(a.question_id),
      label,
      amount: Math.round(d * 100) / 100,
    });
  }
  return supplements;
}

export function FlexiblePriceWizard({
  pricingLogic,
  basePrice,
  pricingAnswers,
  setPricingAnswers,
  onDeltaChange,
  autoCommitMulti = false,
}) {
  // Domande ordinate per `order` (fallback all'ordine originale).
  const sortedQuestions = useMemo(() => {
    if (!Array.isArray(pricingLogic)) return [];
    return [...pricingLogic]
      .filter((q) => q && q.id)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [pricingLogic]);

  const rootQuestions = useMemo(
    () =>
      sortedQuestions.filter(
        (q) => q.is_root || !q.parent_question_id || !q.parent_answer_id,
      ),
    [sortedQuestions],
  );

  const walked = useMemo(() => {
    if (rootQuestions.length === 0) return [];
    const sequence = [];
    const visited = new Set();
    const answersMap = new Map(
      (pricingAnswers || [])
        .filter((a) => a && a.question_id)
        .map((a) => [sid(a.question_id), a]),
    );
    const pushChain = (question) => {
      if (!question) return;
      const qid = sid(question.id);
      if (visited.has(qid)) return;
      visited.add(qid);
      const answered = answersMap.get(qid);
      sequence.push({ q: question, answered });
      if (!answered) return;
      const primary = primaryAnswerId(answered, question);
      // Iter209h: per multi-select seguiamo la risposta con posizione più bassa
      const children = sortedQuestions.filter(
        (c) =>
          sid(c.parent_question_id) === qid && sid(c.parent_answer_id) === primary,
      );
      for (const child of children) pushChain(child);
    };
    for (const root of rootQuestions) pushChain(root);
    return sequence;
  }, [rootQuestions, sortedQuestions, pricingAnswers]);

  const totalDelta = useMemo(() => {
    let running = Number(basePrice) || 0;
    let delta = 0;
    for (const { answered, q } of walked) {
      if (!answered) continue;
      const d = computeQuestionDelta(answered, q, running);
      running = Math.round((running + d) * 100) / 100;
      delta += d;
    }
    return Math.round(delta * 100) / 100;
  }, [walked, basePrice]);

  useEffect(() => {
    if (typeof onDeltaChange === "function") onDeltaChange(totalDelta);
  }, [totalDelta, onDeltaChange]);

  const currentIdx = walked.findIndex((w) => !w.answered);
  const done = currentIdx === -1 && walked.length > 0;

  const collectDescendantIds = (qid) => {
    const acc = new Set();
    const walk = (parentId) => {
      for (const c of sortedQuestions) {
        if (sid(c.parent_question_id) === sid(parentId) && !acc.has(sid(c.id))) {
          acc.add(sid(c.id));
          walk(c.id);
        }
      }
    };
    walk(qid);
    return acc;
  };

  // === Single-select: commit immediato dell'answer ===
  const selectAnswer = (q, a) => {
    const qid = sid(q.id);
    const descendants = collectDescendantIds(q.id);
    const keep = (pricingAnswers || []).filter((x) => {
      const xqid = sid(x.question_id);
      if (xqid === qid) return false;
      return !descendants.has(xqid);
    });
    keep.push({
      question_id: qid,
      question_label: q.question || "",
      answer_id: sid(a.id),
      answer_label: a.label || "",
      price_modifier_type: a.price_modifier?.type || "pct",
      price_modifier_value: Number(a.price_modifier?.value || 0),
    });
    setPricingAnswers(keep);
  };

  // === Multi-select: staging locale + commit on "Continua" ===
  // pending[qid] = Set<answer_id>
  const [pending, setPending] = useState({});
  const togglePending = (qid, aid) => {
    setPending((p) => {
      const s = new Set(p[sid(qid)] || []);
      if (s.has(sid(aid))) s.delete(sid(aid));
      else s.add(sid(aid));
      return { ...p, [sid(qid)]: s };
    });
  };

  const commitMulti = (q) => {
    const qid = sid(q.id);
    const idsSet = pending[qid] || new Set();
    if (idsSet.size === 0) return;
    // Preserva ordine originale delle answers
    const orderedIds = (q.answers || [])
      .map((a) => sid(a.id))
      .filter((id) => idsSet.has(id));
    const selectedDefs = (q.answers || []).filter((a) => idsSet.has(sid(a.id)));
    const descendants = collectDescendantIds(q.id);
    const keep = (pricingAnswers || []).filter((x) => {
      const xqid = sid(x.question_id);
      if (xqid === qid) return false;
      return !descendants.has(xqid);
    });
    keep.push({
      question_id: qid,
      question_label: q.question || "",
      answer_ids: orderedIds,
      answer_labels: selectedDefs.map((a) => a.label || ""),
      selected_answers: selectedDefs.map((a) => ({
        id: sid(a.id),
        label: a.label || "",
        price_modifier: a.price_modifier || {},
      })),
      is_multi_answer: true,
    });
    setPricingAnswers(keep);
    // Ripulisce lo staging per la domanda appena committata
    setPending((p) => { const n = { ...p }; delete n[qid]; return n; });
  };

  // Iter209ec — Multi-select con salvataggio immediato ad ogni spunta.
  const toggleAuto = (q, a) => {
    const qid = sid(q.id);
    const list = pricingAnswers || [];
    const prev = list.find((x) => sid(x.question_id) === qid);
    const ids = new Set((prev?.answer_ids || []).map(sid));
    if (ids.has(sid(a.id))) ids.delete(sid(a.id)); else ids.add(sid(a.id));
    const selectedDefs = (q.answers || []).filter((x) => ids.has(sid(x.id)));
    const entry = {
      question_id: qid,
      question_label: q.question || "",
      answer_ids: selectedDefs.map((x) => sid(x.id)),
      answer_labels: selectedDefs.map((x) => x.label || ""),
      selected_answers: selectedDefs.map((x) => ({ id: sid(x.id), label: x.label || "", price_modifier: x.price_modifier || {} })),
      is_multi_answer: true,
    };
    const primaryChanged = (prev?.answer_ids?.[0] ?? null) !== (entry.answer_ids[0] ?? null);
    const descendants = primaryChanged ? collectDescendantIds(q.id) : new Set();
    const next = [];
    for (const x of list) {
      const xqid = sid(x.question_id);
      if (descendants.has(xqid)) continue;
      if (xqid === qid) { if (entry.answer_ids.length) next.push(entry); continue; }
      next.push(x);
    }
    if (!prev && entry.answer_ids.length) next.push(entry);
    setPricingAnswers(next);
  };

  const reset = () => {
    setPricingAnswers([]);
    setPending({});
  };

  const editFrom = (idx) => {
    setPricingAnswers((answers) => answers.slice(0, idx));
    setPending({});
  };

  const editBtn = (idx) => (
    <button
      type="button"
      onClick={() => editFrom(idx)}
      className="sm:hidden ml-auto shrink-0 inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full border border-indigo-200 text-[10px] font-semibold text-indigo-700 hover:bg-indigo-50"
      data-testid={`flex-price-edit-${idx}`}
    >
      <PencilSimple size={10} /> Modifica
    </button>
  );

  if (!Array.isArray(pricingLogic) || pricingLogic.length === 0) return null;

  // Helper: formatta un modifier per il breadcrumb
  const fmtModifier = (type, val) => {
    if (!val) return "";
    const sign = val > 0 ? "+" : "−";
    const abs = Math.abs(val);
    if ((type || "").toLowerCase() === "pct") {
      return `${sign} ${abs.toLocaleString("it-IT", { maximumFractionDigits: 2 })}%`;
    }
    return `${sign} ${abs.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  };

  return (
    <div
      className="p-3 rounded-lg border-2 border-indigo-300 bg-indigo-50/40"
      data-testid="flexible-price-wizard"
    >
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-sm font-semibold text-indigo-900">
            💬 Personalizza il servizio
          </div>
          <div className="text-[11px] text-indigo-700">
            Rispondi alle domande per calcolare il prezzo esatto del tuo caso.
          </div>
        </div>
        {(pricingAnswers || []).length > 0 && (
          <button
            type="button"
            onClick={reset}
            className="hidden sm:inline-flex text-[11px] text-indigo-600 hover:underline items-center gap-1"
            data-testid="flex-price-reset"
          >
            <ArrowClockwise size={11} /> Ricomincia
          </button>
        )}
      </div>

      {/* Breadcrumb */}
      {(pricingAnswers || []).length > 0 && (
        <div className="mb-3 space-y-1" data-testid="flex-price-history">
          {pricingAnswers.map((pa, idx) => {
            if (pa.is_multi_answer) {
              const labels = (pa.answer_labels || []).join(" + ") || "—";
              // Sum per tipo per l'etichetta modifier aggregata
              let eurSum = 0;
              let pctSum = 0;
              for (const sa of pa.selected_answers || []) {
                const mod = sa.price_modifier || {};
                const t = (mod.type || "").toLowerCase();
                const v = Number(mod.value) || 0;
                if (t === "eur") eurSum += v;
                else if (t === "pct") pctSum += v;
              }
              const bits = [];
              if (eurSum !== 0) bits.push(fmtModifier("eur", eurSum));
              if (pctSum !== 0) bits.push(fmtModifier("pct", pctSum));
              const label = bits.join(" · ");
              return (
                <div
                  key={`${pa.question_id}-${idx}`}
                  className="flex items-start gap-2 text-[11px] text-slate-700"
                  data-testid={`flex-price-answered-${idx}`}
                >
                  <CheckCircle size={12} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                  <span>
                    <strong>{pa.question_label}:</strong> {labels}
                    {label && (
                      <span
                        className="ml-1 text-[10px] font-semibold text-rose-700"
                        data-testid={`flex-price-modifier-${idx}`}
                      >
                        ({label})
                      </span>
                    )}
                  </span>
                  {editBtn(idx)}
                </div>
              );
            }
            const type = (pa.price_modifier_type || "").toLowerCase();
            const val = Number(pa.price_modifier_value) || 0;
            const positive = val > 0;
            const negative = val < 0;
            const modifierLabel = fmtModifier(type, val);
            return (
              <div
                key={`${pa.question_id}-${idx}`}
                className="flex items-start gap-2 text-[11px] text-slate-700"
                data-testid={`flex-price-answered-${idx}`}
              >
                <CheckCircle size={12} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                <span>
                  <strong>{pa.question_label}:</strong> {pa.answer_label}
                  {modifierLabel && (
                    <span
                      className={
                        "ml-1 text-[10px] font-semibold " +
                        (positive
                          ? "text-rose-700"
                          : negative
                          ? "text-emerald-700"
                          : "text-slate-500")
                      }
                      data-testid={`flex-price-modifier-${idx}`}
                    >
                      ({modifierLabel})
                    </span>
                  )}
                </span>
                {editBtn(idx)}
              </div>
            );
          })}
        </div>
      )}

      {walked.some((w) => !w.answered || (autoCommitMulti && w.q.allow_multiple_answers)) && (
        <div className="space-y-3" data-testid="flex-price-open-questions">
          {walked
            .map((w, idx) => ({ ...w, idx }))
            .filter((w) => !w.answered || (autoCommitMulti && w.q.allow_multiple_answers))
            .map(({ q, idx }) => {
              const isMulti = !!q.allow_multiple_answers;
              const qid = sid(q.id);
              const pendingSet = autoCommitMulti
                ? new Set(((pricingAnswers || []).find((x) => sid(x.question_id) === qid)?.answer_ids || []).map(sid))
                : (pending[qid] || new Set());
              return (
                <div key={q.id} data-testid={`flex-price-question-${idx}`}>
                  <div className="text-sm font-medium text-slate-900 mb-2">
                    {q.question || "Domanda"}
                    {isMulti && (
                      <span
                        className="ml-2 text-[9px] font-bold uppercase tracking-wider bg-indigo-600 text-white px-1.5 py-0.5 rounded"
                        data-testid={`flex-price-multi-badge-${idx}`}
                      >
                        Multi
                      </span>
                    )}
                  </div>
                  {isMulti ? (
                    <>
                      <div className="flex flex-col gap-2">
                        {(q.answers || []).map((a, aidx) => {
                          const checked = pendingSet.has(sid(a.id));
                          return (
                            <label
                              key={a.id}
                              className={
                                "flex items-center gap-2 px-3 py-2 rounded-md border cursor-pointer transition text-sm " +
                                (checked
                                  ? "border-indigo-500 bg-indigo-100 text-indigo-900"
                                  : "border-indigo-300 bg-white hover:bg-indigo-50 text-slate-800")
                              }
                              data-testid={`flex-price-checkbox-label-${idx}-${aidx}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => (autoCommitMulti ? toggleAuto(q, a) : togglePending(q.id, a.id))}
                                className="h-4 w-4 accent-indigo-600"
                                data-testid={`flex-price-checkbox-${idx}-${aidx}`}
                              />
                              <span className="flex-1">{a.label || `Opzione ${aidx + 1}`}</span>
                            </label>
                          );
                        })}
                      </div>
                      {!autoCommitMulti && <div className="mt-2 flex justify-end">
                        <button
                          type="button"
                          onClick={() => commitMulti(q)}
                          disabled={pendingSet.size === 0}
                          className={
                            "px-3 py-1.5 rounded-md text-sm font-semibold transition " +
                            (pendingSet.size === 0
                              ? "bg-indigo-200 text-indigo-500 cursor-not-allowed"
                              : "bg-indigo-600 text-white hover:bg-indigo-700")
                          }
                          data-testid={`flex-price-multi-continue-${idx}`}
                        >
                          Continua →
                        </button>
                      </div>}
                    </>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {(q.answers || []).map((a, aidx) => (
                        <button
                          key={a.id}
                          type="button"
                          onClick={() => selectAnswer(q, a)}
                          className="px-3 py-2 rounded-md border border-indigo-300 bg-white hover:bg-indigo-100 text-sm text-indigo-900 transition"
                          data-testid={`flex-price-answer-${idx}-${aidx}`}
                        >
                          {a.label || `Opzione ${aidx + 1}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      )}

      {done && (
        <div
          className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1"
          data-testid="flex-price-done"
        >
          {/* Iter209aa — Totale finale = prezzo base + somma di tutti i
              supplementi (`totalDelta` già include i modificatori EUR e %
              secondo la stessa regola applicata al preview live). */}
          ✓ Configurazione completata. Totale:{" "}
          <strong data-testid="flex-price-done-total">
            €{(Math.round(((Number(basePrice) || 0) + (Number(totalDelta) || 0)) * 100) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </strong>
        </div>
      )}
    </div>
  );
}

export default FlexiblePriceWizard;
